<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLocale, useLocalisation } from '@/plugins/translation';
import AreaGuideNavigation from '@/components/area-guide/AreaGuideNavigation.vue';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import config from '@/config/ConfigProvider';
import useKeywordsStore, { KeywordWithTranslations } from '@/store/keywords';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import useHasLibrary from '@/composables/use-has-library';
import AreaGuideCarousel from '@/components/area-guide/AreaGuideCarousel.vue';
import type { Video } from '@ilteducation/videos';
import MarkDown from '@/components/MarkDown.vue';
import AreaGuideLanguagePicker from '@/components/area-guide/AreaGuideLanguagePicker.vue';
import { storeToRefs } from 'pinia';
import type { Language } from '@ilteducation/content-ref-data-types/models/data-types';
import AreaGuideSection from '@/components/area-guide/AreaGuideSection.vue';
import useLanguagesStore from '@/store/languages';
import useAreaTranslations from '@/store/area-translations';
import { useDisplay } from 'vuetify';
import useIsRtlLanguage from '@/composables/use-is-rtl-language';
import { useVideos } from '@ilteducation/videos';
import useGuiStore from '@/store/gui';
import SimilarContentList from '@/components/SimilarContentList.vue';
import { useImages } from '@ilteducation/images';
import { useAreaGuidesAnalytics } from '@/composables/use-area-guides-analytics';
import { useHead } from '@unhead/vue';
import capitalize from 'lodash/capitalize';

const route = useRoute();
const router = useRouter();
const videoStore = useVideos();
const { areas } = useAreas();
const { categories } = useCategories();
const { languages } = storeToRefs(useLanguagesStore());
const { mdAndUp, width } = useDisplay();
const iPadWidth = 1024;
const sideBySideLanguage = ref<Language>();
const showMultipleLanguages = ref<boolean>(false);
const showTranslatedTexts = computed(() => !!(showMultipleLanguages.value && sideBySideLanguage.value));
const { includeAdditionalProject, language: currentLanguage } = useLocalisation();
const { areaTranslations, areaGuideLanguages } = storeToRefs(useAreaTranslations());
const translatedLanguages = ref<string[]>([]);
const availableLanguages = computed(() =>
  languages.value.filter(
    (language) =>
      translatedLanguages.value.includes(language.languageCode) &&
      areaGuideLanguages?.value?.includes(language.languageCode) &&
      language.languageCode !== currentLanguage.value.split('-')[0],
  ),
);
const { getAreaTranslation } = useAreaTranslations();
const isRTL = computed(
  () => sideBySideLanguage.value?.languageCode && useIsRtlLanguage(sideBySideLanguage.value.languageCode),
);
const { getMarketLanguage } = useLanguagesStore();

const { t, tm, l } = useLocale();
const hasLibrary = useHasLibrary();

const isScrolling = ref<boolean>(false);
const selectedTab = ref<string>('');
const activeSectionId = ref<number>(0);

const areaFromRoute = computed(() => route.params.area);

const subscriptions = useSubscriptions();
const { load: loadCategories } = useCategories();
const { load: loadAreas } = useAreas();
const {
  setAreaId,
  sendGoToSectionEvent,
  sendClickLanguageButtonEvent,
  sendSelectLanguageEvent,
  sendToggleLanguageEvent,
  sendClickCarouselEvent,
  sendScrollCarouselEvent,
  sendClickKeywordEvent,
  sendClickVideoEvent,
  sendClickBookEvent,
} = useAreaGuidesAnalytics();

const subject = computed(() =>
  categories.value.find((availableSubject) => availableSubject.slug === route.params.subject),
);

const subjectColor = computed(() => subject?.value?.color);
const relatedBooksRef = ref();
const areaHasRelatedBooks = computed(() => !!relatedBooksRef.value?.hasRelatedContents);

const area = computed(() => {
  const subjectAreas = areas.value.filter((subjectArea) => subjectArea.categoryId === subject.value?.id);
  return subjectAreas.find((subjectArea) => subjectArea.slug === areaFromRoute.value);
});
const translations = computed(() => (key: string) => !area.value || tm(area.value.id)[key]);
const areaVideos = ref<Video[]>([]);

const serviceName = config.value('serviceName');
useHead({
  title: computed(() =>
    area.value
      ? t(l.area_guide.page_title, { area: area.value.name, service: capitalize(serviceName) })
      : capitalize(serviceName),
  ),
});

const breadcrumbsSteps = computed(() => [
  {
    name: subject.value?.category ?? '',
    path: `/videos/${subject.value?.slug ?? ''}`,
  },
]);

const keywordsStore = useKeywordsStore();
const areaKeywordsWithLongDescriptions = ref<KeywordWithTranslations[]>([]);
const altTexts = ref<Record<string, string>>({});

watch(areaKeywordsWithLongDescriptions, () => {
  selectedTab.value = areaKeywordsWithLongDescriptions.value[0]?.id ?? '';
});

watch(
  areaVideos,
  async (af) => {
    if (af.length > 0) {
      const keywordIds = Array.from(new Set(af.flatMap((film) => film.keywords)));
      const kws = await useKeywordsStore().getKeywordTranslations(keywordIds);
      areaKeywordsWithLongDescriptions.value = kws
        .filter((kw) => kw.descriptionLong)
        .toSorted((a, b) => a.title.localeCompare(b.title));
      const images = kws.map((k) => k.imageId).filter((id): id is string => !!id);
      altTexts.value = await useImages().getAltTextsTranslations(images, getMarketLanguage());
    }
  },
  { immediate: true },
);

const scrollToSection = (target?: number | string) => {
  if (!target || isScrolling.value) {
    return;
  }
  isScrolling.value = true;

  const isAnchor = typeof target === 'string';
  const from = activeSectionId.value;
  activeSectionId.value = isAnchor ? parseFloat(target.replace('#section', '').replace('-', '.')) : target;
  const element = document.querySelector(isAnchor ? target : `#section${target.toString().replace('.', '-')}`);
  if (!element) {
    isScrolling.value = false;
    return;
  }
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'end',
  });
  if (from !== target) {
    sendGoToSectionEvent(activeSectionId.value, from);
  }

  router.replace({ path: route.path, hash: isAnchor ? target : `#section${target.toString().replace('.', '-')}` });

  if (!(element instanceof HTMLElement)) {
    isScrolling.value = false;
    return;
  }

  let safetyTimeoutId: ReturnType<typeof setTimeout> | undefined;
  const observer = new IntersectionObserver(
    ([entry], obs) => {
      if (entry.isIntersecting) {
        element.setAttribute('tabindex', '-1');
        setTimeout(() => element.removeAttribute('tabindex'), 1000);
        element.focus();
        obs.disconnect();
        clearTimeout(safetyTimeoutId);
        isScrolling.value = false;
      }
    },
    { threshold: 1.0 },
  );
  observer.observe(element);

  // Safety-net fallback: covers cases where the target section is taller than the
  // viewport and can never reach full visibility, so the observer above never fires.
  // Always disconnects the observer too, so it can't linger and fire later for an
  // unrelated reason (resize, reflow) once this scroll cycle is done.
  safetyTimeoutId = setTimeout(() => {
    observer.disconnect();
    isScrolling.value = false;
  }, 2000);
};

const scrollToNextSection = (fromSectionAnchor: string) => {
  const nextSectionId = document.querySelector(`${fromSectionAnchor} + section`)?.id;
  scrollToSection(`#${nextSectionId}`);
};

const onIntersect = (isIntersecting: boolean, entries: IntersectionObserverEntry[]) => {
  if (isScrolling.value) {
    return;
  }
  entries.forEach((entry: IntersectionObserverEntry) => {
    if (entry.intersectionRatio >= 0.5) {
      const index = entry.target.getAttribute('data-index');
      if (activeSectionId.value === 0) {
        activeSectionId.value = 1.1;
        return;
      }

      if (index !== null && parseFloat(index) !== activeSectionId.value && activeSectionId.value !== 0) {
        const from = activeSectionId.value;
        history.replaceState({}, '', `${route.path}#section${index.toString().replace('.', '-')}`);
        activeSectionId.value = parseFloat(index);
        sendGoToSectionEvent(activeSectionId.value, from);
      }
    }
  });
};

const selectTab = (uid: string) => {
  sendClickKeywordEvent(uid, selectedTab.value);
  selectedTab.value = uid;
  // Set focus to v-card when a keyword is selected and set aria-hidden to false
  window.setTimeout(() => {
    const element = document.getElementById(`keyword-${uid}-tab`);
    if (element && element instanceof HTMLElement) {
      element.setAttribute('aria-hidden', 'false');
      element.setAttribute('tabindex', '-1');
      element.focus();
    }
  }, 0);
};

const focusKeywordList = () => {
  const list = document.querySelector('.keyword-list li:first-child button');
  if (list && list instanceof HTMLButtonElement) {
    list.focus();
  }
};

const hasCarousel = computed(() => !!area.value?.carouselKeywords?.length);

onMounted(async () => {
  await Promise.all([loadCategories(subscriptions.value[0].type), loadAreas()]);
  if (route.hash) {
    await scrollToSection(route.hash);
  }
  await includeAdditionalProject(config.value('guidesLokaliseProject'));
});

watch(
  area,
  async (loadedArea) => {
    if (!loadedArea) {
      return;
    }
    const response = await Promise.allSettled(
      areaGuideLanguages.value.map((lang) => getAreaTranslation(loadedArea.id, lang)),
    );
    translatedLanguages.value = response
      .map((r) => (r.status === 'fulfilled' ? r.value : undefined))
      .filter((lang) => lang) as string[];
    areaVideos.value = await videoStore.getVideosByAreaId(loadedArea.id);
    setAreaId(loadedArea.id);
  },
  { immediate: true },
);

const areaTextTranslations = computed(() => (key: string) => {
  if (!area.value || !sideBySideLanguage.value || !areaTranslations.value) {
    return '';
  }
  return areaTranslations.value[area.value?.id]?.[sideBySideLanguage.value?.languageCode]?.[key];
});

const getKeywordTranslation = (k: KeywordWithTranslations, part: string) => {
  if (!sideBySideLanguage.value?.languageCode || !(part in k.translations)) {
    return '';
  }
  const languageCode = sideBySideLanguage.value.languageCode;
  return k.translations[part]?.[languageCode]?.translation;
};

const handleLanguageButtonClicked = () => {
  sendClickLanguageButtonEvent(sideBySideLanguage.value?.languageCode);
};

const handleLanguageSelection = (language: Language) => {
  showMultipleLanguages.value = true;
  sendSelectLanguageEvent(language.languageCode, sideBySideLanguage.value?.languageCode);
  sideBySideLanguage.value = language;
};

const handleToggleLanguage = () => {
  if (!sideBySideLanguage.value) {
    return;
  }
  sendToggleLanguageEvent(sideBySideLanguage.value.languageCode, !showMultipleLanguages.value ? 'on' : 'off');
};

const handleCarouselClick = (carouselKeywordId: string, carouselPosition: number) => {
  sendClickCarouselEvent(carouselKeywordId, carouselPosition);
};

const handleCarouselScrolled = (scrollDirection: 'next' | 'prev', from: number, to: number) => {
  sendScrollCarouselEvent(scrollDirection, from, to);
};

const handleClickVideo = (videoId: string, videoSlug: string) => {
  sendClickVideoEvent(videoId);
  router.push({
    name: 'showFilm',
    params: {
      subject: route.params.subject,
      area: route.params.area,
      film: videoSlug,
    },
  });
};

const handleBookClick = (bookId: string) => {
  sendClickBookEvent(bookId);
};

const { getThemeName } = useGuiStore();
</script>

<template>
  <v-card :theme="getThemeName(subject?.slug)" v-if="subject && area" elevation="0" class="bg-white">
    <v-container class="pa-md-0">
      <AreaGuideNavigation
        :active-section-id="activeSectionId"
        :breadcrumbs-steps="breadcrumbsSteps"
        :current-subject="subject"
        :current-area="area"
        :has-carousel="hasCarousel"
        :has-textbooks="areaHasRelatedBooks"
        @scroll-to-anchor="(item) => scrollToSection(item.id)"
      >
        <template v-if="mdAndUp" #bottom>
          <v-tooltip location="top" aria-hidden="true">
            <template #activator="{ props }">
              <v-switch
                v-if="sideBySideLanguage"
                v-model="showMultipleLanguages"
                :color="subjectColor"
                hide-details
                class="d-flex justify-space-between"
                v-bind="props"
                @click="handleToggleLanguage"
              >
                <template #prepend>
                  {{ t(l.area_guide.show_supporting_language) }}
                </template>
              </v-switch>
            </template>
            {{ t(l.area_guide.tooltip_show_supporting_language) }}
          </v-tooltip>
          <AreaGuideLanguagePicker
            :color="subjectColor"
            :languages="availableLanguages"
            @language-button-clicked="handleLanguageButtonClicked"
            @language-selected="handleLanguageSelection"
          />
        </template>
      </AreaGuideNavigation>
    </v-container>
    <v-container fluid class="bg-surface-bright content px-4 px-sm-16 py-8">
      <AreaGuideSection
        index="1.1"
        :title="t(l.area_guide.navigation.item_1_1)"
        icon="fa-solid fa-thought-bubble"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <v-container class="px-0 d-flex gc-6 flex-wrap flex-md-nowrap">
          <v-card color="surface-bright" elevation="3" rounded class="statement mb-8 pa-6 pa-sm-8 overflow-visible">
            <MarkDown class="" :description="translations('description')" />
          </v-card>
          <v-card v-if="showTranslatedTexts" elevation="0" class="statement mb-8 pa-6 pa-sm-8 translated-content">
            <MarkDown :dir="isRTL ? 'rtl' : 'ltr'" :description="areaTextTranslations('description')" />
          </v-card>
        </v-container>
      </AreaGuideSection>

      <AreaGuideSection
        :title="t(l.area_guide.navigation.item_1_3)"
        index="1.3"
        icon="fa-solid fa-thought-bubble"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <p class="mb-8">{{ t(l.area_guide.click_to_see) }}</p>
        <AreaGuideCarousel
          :show-translations="showTranslatedTexts"
          :selected-language="sideBySideLanguage?.languageCode"
          :carouselItems="area.carouselKeywords"
          :alt-texts="altTexts"
          @carousel-clicked="handleCarouselClick"
          @carousel-scrolled="handleCarouselScrolled"
        />
      </AreaGuideSection>

      <AreaGuideSection
        :title="t(l.area_guide.navigation.item_1_2)"
        icon="fa-solid fa-thought-bubble"
        index="1.2"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <template v-if="mdAndUp">
          <v-row
            v-for="(question, idx) in translations('reflect')"
            :key="question"
            no-gutters
            class="rounded gc-4 mb-4 d-flex"
          >
            <v-col :cols="showMultipleLanguages ? 6 : 12" class="">
              <v-card elevation="3" rounded class="question pa-6 pa-sm-8">
                <p>{{ question }}</p>
              </v-card>
            </v-col>
            <v-col v-if="showMultipleLanguages">
              <v-card
                :dir="isRTL ? 'rtl' : 'ltr'"
                elevation="0"
                rounded
                class="question pa-6 pa-sm-8 translated-content"
              >
                <p>{{ areaTextTranslations('reflect')?.[idx] }}</p>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-container
            v-for="(question, idx) in translations('reflect')"
            :key="question"
            no-gutters
            class="rounded d-flex flex-column ga-4 align-stretch"
          >
            <v-card elevation="3" rounded class="question pa-6">
              <p>{{ question }}</p>
            </v-card>
            <v-card
              v-if="showMultipleLanguages"
              :dir="isRTL ? 'rtl' : 'ltr'"
              elevation="0"
              rounded
              class="question pa-6 translated-content"
            >
              <p>{{ areaTextTranslations('reflect')?.[idx] }}</p>
            </v-card>
          </v-container>
        </template>
      </AreaGuideSection>

      <AreaGuideSection
        :title="t(l.area_guide.navigation.item_2_1)"
        icon="fa-solid fa-head-side-heart"
        index="2.1"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <template v-if="mdAndUp">
          <v-row
            v-for="(question, idx) in translations('why-learn')"
            :key="question"
            no-gutters
            class="rounded gc-4 mb-4"
          >
            <v-col :cols="showMultipleLanguages ? 6 : 12">
              <v-card elevation="3" rounded class="question pa-6 pa-sm-8">
                <p>{{ question }}</p>
              </v-card>
            </v-col>
            <v-col v-if="showMultipleLanguages && areaTextTranslations('why-learn')?.[idx]">
              <v-card elevation="0" rounded class="question pa-6 pa-sm-8 translated-content">
                <p :dir="isRTL ? 'rtl' : 'ltr'">{{ areaTextTranslations('why-learn')[idx] }}</p>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-container
            v-for="(question, idx) in translations('why-learn')"
            :key="question"
            no-gutters
            class="rounded d-flex flex-column ga-4 align-stretch"
          >
            <v-card elevation="3" rounded class="question pa-6">
              <p>{{ question }}</p>
            </v-card>
            <v-card
              v-if="showMultipleLanguages && areaTextTranslations('why-learn')?.[idx]"
              :dir="isRTL ? 'rtl' : 'ltr'"
              elevation="0"
              rounded
              class="question pa-6 translated-content"
            >
              <p :dir="isRTL ? 'rtl' : 'ltr'">{{ areaTextTranslations('why-learn')[idx] }}</p>
            </v-card>
          </v-container>
        </template>
      </AreaGuideSection>

      <AreaGuideSection
        :title="t(l.area_guide.navigation.item_2_2)"
        icon="fa-solid fa-head-side-heart"
        index="2.2"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <template v-if="mdAndUp">
          <v-row
            v-for="(question, idx) in translations('did-you-know')"
            :key="question"
            no-gutters
            class="rounded gc-4 mb-4"
          >
            <v-col :cols="showMultipleLanguages ? 6 : 12">
              <v-card rounded elevation="3" class="question pa-6"
                ><p>{{ question }}</p>
              </v-card>
            </v-col>
            <v-col v-if="showMultipleLanguages && areaTextTranslations('did-you-know')?.[idx]">
              <v-card rounded elevation="0" class="question pa-6 pa-sm-8 translated-content">
                <p :dir="isRTL ? 'rtl' : 'ltr'">{{ areaTextTranslations('did-you-know')[idx] }}</p>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-container
            v-for="(question, idx) in translations('did-you-know')"
            :key="question"
            no-gutters
            class="rounded d-flex flex-column ga-4 align-stretch"
          >
            <v-card elevation="3" rounded class="question pa-6">
              <p>{{ question }}</p>
            </v-card>
            <v-card
              v-if="showMultipleLanguages && areaTextTranslations('did-you-know')?.[idx]"
              :dir="isRTL ? 'rtl' : 'ltr'"
              elevation="0"
              rounded
              class="question pa-6 translated-content"
            >
              <p :dir="isRTL ? 'rtl' : 'ltr'">{{ areaTextTranslations('did-you-know')[idx] }}</p>
            </v-card>
          </v-container>
        </template>
      </AreaGuideSection>

      <AreaGuideSection
        :title="t(l.area_guide.navigation.item_2_3)"
        icon="fa-solid fa-head-side-heart"
        index="2.3"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <div class="circle mb-8 rounded-circle elevation-3">
          <div class="circle--content pa-10 pa-md-15">
            <span class="h3 text-primary">
              {{ translations('question-for-student') }}
            </span>
            <span v-if="showMultipleLanguages" :dir="isRTL ? 'rtl' : 'ltr'" class="mt-2 h4 text-primary">
              <v-divider class="mb-4 w-100" :aria-label="t(l.label_language_divider)"></v-divider>
              {{ areaTextTranslations('question-for-student') }}
            </span>
          </div>
        </div>
      </AreaGuideSection>

      <AreaGuideSection
        :color="subjectColor"
        :title="t(l.area_guide.navigation.item_4)"
        icon="fa-solid fa-square-list"
        index="4"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <v-container class="limited-container pa-0 ml-0 mb-8">
          <v-row :class="{ 'd-none': width > iPadWidth }">
            <v-col>
              <v-chip
                v-for="keyword in areaKeywordsWithLongDescriptions"
                :key="`chip-${keyword.id}`"
                :class="[selectedTab === keyword.id ? 'active' : `bg-surface-bright`]"
                class="keyword-chip text-default"
                @click="selectTab(keyword.id)"
                >{{ keyword.translations.title?.[getMarketLanguage()].translation }}</v-chip
              >
            </v-col>
          </v-row>
          <v-row>
            <v-col order="last" cols="4" :class="{ 'd-none': width <= iPadWidth }">
              <nav>
                <ul class="keyword-list" :aria-label="t(l.area_guide.navigation.item_4)">
                  <li
                    v-for="keyword in areaKeywordsWithLongDescriptions"
                    :key="keyword.id"
                    class="rounded"
                    :class="{ 'bg-primary active': selectedTab === keyword.id }"
                  >
                    <v-btn
                      block
                      :ripple="false"
                      color="transparent"
                      size="x-large"
                      elevation="0"
                      @click="selectTab(keyword.id)"
                      >{{ keyword.translations?.title?.[getMarketLanguage()]?.translation }}
                    </v-btn>
                  </li>
                </ul>
              </nav>
            </v-col>
            <v-col order="last" order-md="first" cols="12" md="8" class="px-0 pt-0 pt-md-3">
              <template v-for="keyword in areaKeywordsWithLongDescriptions">
                <v-card
                  v-if="selectedTab === keyword.id"
                  :id="`keyword-${keyword.id}-tab`"
                  :key="keyword.id"
                  class="tab-container mt-0 overflow-visible"
                  :aria-hidden="true"
                >
                  <v-card-title class="font-weight-bold pt-5 px-5"
                    >{{ keyword.translations?.title?.[getMarketLanguage()].translation }}
                    <span
                      v-if="
                        showTranslatedTexts &&
                        sideBySideLanguage?.languageCode &&
                        getKeywordTranslation(keyword, 'title')
                      "
                      class="text-grey"
                    >
                      / {{ getKeywordTranslation(keyword, 'title') }}</span
                    >
                  </v-card-title>
                  <v-card-text class="px-5">
                    <p v-if="keyword.imageId" class="mb-5">
                      <v-img :src="keywordsStore.getKeywordImageUrl(keyword)" :alt="altTexts[keyword.imageId]" />
                    </p>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div class="keyword-text">
                      <MarkDown
                        :description="keyword.translations?.descriptionLong?.[getMarketLanguage()]?.translation"
                      />
                      <template
                        v-if="
                          showTranslatedTexts &&
                          sideBySideLanguage?.languageCode &&
                          getKeywordTranslation(keyword, 'descriptionLong')
                        "
                      >
                        <v-divider class="mb-3 w-100" :aria-label="t(l.label_language_divider)"></v-divider>
                        <MarkDown
                          :dir="isRTL ? 'rtl' : 'ltr'"
                          :support-language="showTranslatedTexts ? sideBySideLanguage?.languageCode : ''"
                          :description="getKeywordTranslation(keyword, 'descriptionLong')"
                        />
                      </template>
                    </div>
                    <v-btn
                      class="d-sr-only"
                      tabindex="0"
                      @click="focusKeywordList"
                      :aria-label="t(l.area_guide.label_go_back_to_keyword_nav)"
                    >
                    </v-btn>
                  </v-card-text>
                </v-card>
              </template>
            </v-col>
          </v-row>
        </v-container>
      </AreaGuideSection>

      <AreaGuideSection
        :title="t(l.area_guide.navigation.item_3)"
        icon="fa-solid fa-video"
        index="3"
        @intersect="onIntersect"
        @nextSection="scrollToNextSection"
      >
        <v-container fluid class="limited-container pa-0 ml-0 mb-8">
          <v-row>
            <v-col v-for="video in areaVideos" :key="video.id" cols="12" sm="6" md="5" xl="4" class="pr-5">
              <v-card @click="handleClickVideo(video.id, video.slug)" class="movie-card">
                <v-img :src="video.thumbnail" alt="" />
                <v-card-title class="card-title pa-3 word-break-normal">{{ video.title }}</v-card-title>
                <v-card-subtitle class="card-subtitle ma-0 px-3 pb-3 text-black">
                  {{ video.description }}
                </v-card-subtitle>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </AreaGuideSection>

      <AreaGuideSection
        v-if="hasLibrary"
        v-show="areaHasRelatedBooks"
        :title="t(l.film.tabs.immerse)"
        icon="fa-solid fa-book-open-cover"
        index="5"
        @intersect="onIntersect"
      >
        <SimilarContentList
          ref="relatedBooksRef"
          v-if="areaVideos.length"
          :content-ids="areaVideos.map((v) => v.id)"
          :context-key="area.id"
          :grid-props="{ cols: 6, sm: 5, lg: 3, xl: 2 }"
          @book-clicked="handleBookClick"
        ></SimilarContentList>
      </AreaGuideSection>
    </v-container>
  </v-card>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.guide-container {
  height: 100%;
  position: relative;
  overflow-x: clip;
  width: 100dvw !important;

  & > .row {
    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      height: 100%;
    }
  }
}

.greppa {
  font-size: 1.375rem;
}

p,
:deep(.statement p),
:deep(.statement li) {
  font-size: 1.125rem;
  line-height: 1.25;
}

:deep(.keyword-text p) {
  font-size: 1.125rem;
  line-height: 1.25;
  margin-bottom: 16px;
}

:deep(.keyword-text ul) {
  margin-bottom: 16px;
  padding-left: 24px;
}

:deep(.keyword-text ol) {
  margin-bottom: 16px;
  padding-left: 24px;
}

.limited-container {
  max-width: 1200px;
}

.content {
  position: relative;
  min-height: 100%;
  overflow: hidden;

  &:before {
    left: -55px;
    top: -75px;
    width: 795px;
    height: 795px;
    background: rgba(var(--v-theme-surface-container));
    border-radius: 50%;
    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      content: '';
      position: absolute;
    }
  }

  .statement,
  .question,
  .circle {
    max-width: 600px;
    background: white;
  }

  .question {
    p {
      font-size: 1.125rem;
      line-height: 1.36;
    }
  }

  .circle {
    width: 100%;
    position: relative;

    &:after {
      content: '';
      display: block;
      clear: both;
      padding-bottom: 100%;
    }

    &--content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
    }
  }

  .movie-card {
    .card-title {
      line-height: 1.15;
    }

    .card-subtitle {
      font-size: 1rem;
      line-height: 1.25rem;
      white-space: wrap;
    }
  }

  .movie-card :deep(.v-img) {
    aspect-ratio: 16 / 9;
  }

  .keyword-list {
    list-style: none;
    max-height: 800px;
    overflow-y: auto;

    .v-btn {
      justify-content: flex-start;
      padding: 10px 16px !important;
      height: 50px !important;
      text-transform: none;
    }
  }

  .keyword-text {
    font-size: 1.125rem;
  }

  .v-card.v-card--link {
    height: 100%;
  }

  .tab-container {
    &.v-card {
      .v-card__title {
        padding: 1.25rem 1.25rem 0;
        line-height: 1.2;
      }

      .v-card__text {
        padding: 1.35rem;

        p {
          font-size: 1.125rem;
          line-height: 1.35;
          margin-bottom: 1.44rem;

          &:last-of-type {
            margin: 0;
          }
        }
      }
    }
  }

  .translated-content {
    background: rgba(255, 255, 255, 0.8);
  }
}

.v-select-list {
  .custom-item {
    border-top: 1px solid #d0d5d9;
    width: calc(100% + 32px);
    display: flex;
    margin: 0 -16px;
    padding: 12px;
    color: rgba(var(--v-theme-primary));
    font-size: 1rem;
    line-height: 1.375;
  }

  .v-list-item--active {
    .custom-item {
      position: relative;

      &:before {
        content: '';
        clear: both;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        opacity: 0.1;
      }

      background: white;
    }
  }
}

li > .v-btn {
  border-radius: 6px;
}

li.active > .v-btn {
  transition: all 0.15s;
}

.word-break-normal {
  word-break: normal;
}

:deep(.keyword-chip) {
  border-radius: 6px;

  &.active {
    background: rgba(var(--v-theme-primary)) !important;
    color: #fffffff0;
  }

  height: 30px;
  padding: 0 10px 0 10px;
  margin: 5px 5px;
  font-size: 1rem;

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    height: 36px;
    padding: 0 10px 0 10px;
    font-size: 1.125rem;
    margin: 5px 10px 5px 0;
  }
}
</style>
